import { Injectable } from '@angular/core';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiInsertMediaDialogComponent } from '@fi-shared/fi-insert-media/fi-insert-media-dialog.component';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { isDefined } from '@fi-shared/utils';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FileType, ObjectTypes } from '@fi-shared/types';
import { IInsertMediaResult, IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FileTypeService } from '@fi-shared/fi-shared-services/file-type.service';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';

interface InsertMediaDialogOptions {
    filesToUpload?: ArrayLike<File>;
    multiple?: boolean;
    accept?: string;
    allowStoreInMyFiles?: boolean;
    fileTypes?: FileType[];
    contentLanguageId?: number;
    restoreFocus?: boolean;

    // invoked when user chooses files from device,
    // return promise with false result for prevent uploading file
    onFilesSelecting?: (files: ArrayLike<File>) => Promise<boolean>;

    // invoked when user selects some media bank files in list and clicks 'Insert' button
    // return promise with false result for prevent inserting selected media bank files
    onMediaBankFilesInserting?: (attachments: IInsertMediaUploadItem[]) => Promise<boolean>;
}

@Injectable()
export class FiInsertMedia2Service {
    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private detectDeviceService: DetectDevicesService,
        private fiBackend: FiBackendService,
        private fileTypeService: FileTypeService,
        private fileUpload2Service: FiFileUpload2Service,
    ) {}

    //#region insert media

    openInsertMediaDialog(opts?: InsertMediaDialogOptions) {
        if (!isDefined(opts)) {
            opts = {};
        }
        if (!isDefined(opts.multiple)) {
            opts.multiple = true;
        }

        return this.fiDialogsService.custom({
            hideHeader: true,
            hideFooter: true,
            yesButtonText: this.appTextService.get('insert'),
            contentComponentRef: FiInsertMediaDialogComponent,
            hideDialogTopBorder: true,
            contentComponentData: {
                filesToUpload: opts.filesToUpload,
                multiple: opts.multiple,
                accept: opts.accept,
                allowStoreInMyFiles: opts.allowStoreInMyFiles,
                allowedFileTypes: opts.fileTypes,
                contentLanguageId: opts.contentLanguageId,
                onFilesSelecting: opts.onFilesSelecting,
                onMediaBankFilesInserting: opts.onMediaBankFilesInserting,
            },
            config: {
                panelClass: ['fi-insert-media-dialog-panel', 'fi-rounded-dialog-panel'],
                minWidth: !this.detectDeviceService.isMobilePhone() ? '1024px' : '400px',
                width: this.detectDeviceService.isMobilePhone() ? '90%' : 'unset',
                maxHeight: 'unset',
                height: '95vh',
                autoFocus: false, //!this.detectDeviceService.isMobilePhone(),
                restoreFocus: isDefined(opts.restoreFocus) ? opts.restoreFocus : false,
            },
        });
    }

    /**
     * Open insert media dialog and then generate image sizes and upload them to upcloud blob storage
     * @param objectType
     * @param opts
     */
    openInsertMediaDialogWithFileSizes(objectType: ObjectTypes, opts?: InsertMediaDialogOptions) {
        if (!isDefined(opts)) {
            opts = {};
        }
        // https://chainformation.atlassian.net/browse/FI-4524
        // https://chainformation.atlassian.net/browse/FI-6164
        opts.allowStoreInMyFiles =
            [
                ObjectTypes.Comment,
                ObjectTypes.CheckPointAnswer,
                ObjectTypes.Checkpoint2Answer,
                ObjectTypes.ChatConversationMessage,
                ObjectTypes.Post,
                ObjectTypes.ForumPost,
            ].indexOf(objectType) === -1;

        return this.openInsertMediaDialog(opts);
    }

    //#endregion

    public onFilePasted(files: File[], objectType: ObjectTypes): Promise<IMediaBankFileReference[]> {
        return new Promise<IMediaBankFileReference[]>((resolve, reject) => {
            if (files.length && files[0].size > this.fileTypeService.getFileSilentUploadLimit() * 1024 * 1024) {
                this.openInsertMediaDialogWithFileSizes(objectType, { filesToUpload: files }).onYesButtonClick(
                    (result: IInsertMediaResult) => {
                        resolve(result.mediaBankFileReferences);
                    },
                );
            } else {
                this.fileUpload2Service
                    .silentUploadFiles(files)
                    .then((mediaBankFileReferences: IMediaBankFileReference[]) => {
                        resolve(mediaBankFileReferences);
                    });
            }
        });
    }
}
